import { create } from "zustand";

// The Grow save chip's note requests while they run, and the note texts they wrote, by notebook id
// (spark#1218). In memory only: a reload ends a run, and its note stays as the save created it.

export interface GrowNoteRun {
  /** The note's line that its own parts could not be written, which Studio hides while they are. */
  line: string;
  /** What the request has written so far, as it streams in. */
  text: string;
}

export interface GrowNoteWritten {
  /** The complete note as genesis took it. */
  text: string;
  /** The note as the save created it: Studio takes `text` over only where it still holds this. */
  initial: string;
}

interface GrowNoteRunsState {
  running: Record<string, GrowNoteRun>;
  /** What a run wrote to genesis, until Studio has taken it over or let it go. */
  written: Record<string, GrowNoteWritten>;
  begin: (notebookId: string, line: string) => void;
  append: (notebookId: string, delta: string) => void;
  /** Ends the run, and in the same update records what it wrote, if anything, so no render sees neither. */
  finish: (notebookId: string, written?: GrowNoteWritten) => void;
  consumeWritten: (notebookId: string) => void;
}

const without = <T,>(record: Record<string, T>, key: string): Record<string, T> =>
  Object.fromEntries(Object.entries(record).filter(([id]) => id !== key));

export const useGrowNoteRuns = create<GrowNoteRunsState>((set) => ({
  running: {},
  written: {},
  begin: (id, line) => set((s) => ({ running: { ...s.running, [id]: { line, text: "" } } })),
  append: (id, delta) =>
    set((s) => {
      const run = s.running[id];
      return run ? { running: { ...s.running, [id]: { ...run, text: run.text + delta } } } : s;
    }),
  finish: (id, written) =>
    set((s) => ({
      running: without(s.running, id),
      ...(written === undefined ? {} : { written: { ...s.written, [id]: written } }),
    })),
  consumeWritten: (id) => set((s) => (id in s.written ? { written: without(s.written, id) } : s)),
}));
